import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import {
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  PencilSquareIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import type { ThemeColors } from '../../context/ThemeContext';
import type { AssistantPendingAction, AssistantReplyLink } from '../../api/assistantApi';

export interface AssistantMessageBubbleProps {
  role: 'user' | 'model';
  text: string;
  theme: ThemeColors;
  /** data-URI of an attached photo, shown as a thumbnail above the text. */
  imageUrl?: string;
  pendingAction?: AssistantPendingAction;
  /** Deep-links into filtered finance pages that back this answer. */
  links?: AssistantReplyLink[];
  /** Action already resolved (any button clicked + API responded) → lock buttons. */
  resolved?: boolean;
  /** An action request for this bubble is in flight → disable + show spinner. */
  busy?: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
  onEdit?: (instruction: string) => void;
}

const AssistantMessageBubble = ({
  role,
  text,
  theme,
  imageUrl,
  pendingAction,
  links,
  resolved = false,
  busy = false,
  onConfirm,
  onCancel,
  onEdit,
}: AssistantMessageBubbleProps) => {
  const isUser = role === 'user';
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(pendingAction?.previewText ?? '');

  const cardBg = theme.components?.card?.background || '#FFFFFF';
  const bubbleBg = isUser ? theme.primary : cardBg;
  const bubbleText = isUser ? '#FFFFFF' : theme.text;
  const border = theme.components?.card?.border || theme.gray?.[200] || '#E5E5E3';

  const submitEdit = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    onEdit?.(trimmed);
    setEditing(false);
  };

  const buttonsDisabled = resolved || busy;

  return (
    <Box
      data-testid="assistant-message"
      data-role={role}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isUser ? 'flex-end' : 'flex-start',
        maxWidth: '100%',
      }}
    >
      {imageUrl && (
        <Box
          component="img"
          data-testid="assistant-message-image"
          src={imageUrl}
          alt="Attached photo"
          sx={{
            maxWidth: '85%',
            maxHeight: 220,
            borderRadius: 2.5,
            border: `1px solid ${border}`,
            mb: text ? 0.5 : 0,
            objectFit: 'cover',
          }}
        />
      )}
      {(text || !imageUrl) && (
        <Box
          sx={{
            maxWidth: '85%',
            px: 1.75,
            py: 1.25,
            borderRadius: 2.5,
            borderTopRightRadius: isUser ? 0.5 : 2.5,
            borderTopLeftRadius: isUser ? 2.5 : 0.5,
            backgroundColor: bubbleBg,
            color: bubbleText,
            border: isUser ? 'none' : `1px solid ${border}`,
            boxShadow: isUser ? 'none' : '0 1px 2px rgba(0,0,0,0.05)',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontSize: '0.9rem',
            lineHeight: 1.5,
          }}
        >
          {text}
        </Box>
      )}

      {!isUser && links && links.length > 0 && (
        <Box
          data-testid="assistant-links"
          sx={{ mt: 0.75, display: 'flex', gap: 0.75, flexWrap: 'wrap', maxWidth: '85%' }}
        >
          {links.map((link) => (
            <Button
              key={link.href}
              data-testid="assistant-link"
              size="small"
              variant="outlined"
              startIcon={<ArrowTopRightOnSquareIcon width={14} />}
              onClick={() => navigate(link.href)}
              sx={{
                color: theme.text,
                borderColor: border,
                textTransform: 'none',
                fontSize: '0.78rem',
                py: 0.25,
                '&:hover': { borderColor: theme.primary },
              }}
            >
              {link.label}
            </Button>
          ))}
        </Box>
      )}

      {pendingAction && (
        <Box
          data-testid="assistant-pending-action"
          sx={{
            mt: 1,
            width: '85%',
            borderRadius: 2.5,
            p: 1.5,
            backgroundColor: 'color-mix(in srgb, var(--action) 10%, transparent)',
            border: `1px solid ${theme.primary}`,
            backdropFilter: 'blur(4px)',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              color: theme.primary,
              mb: 0.5,
            }}
          >
            Proposed action
          </Typography>
          <Typography
            data-testid="assistant-action-preview"
            sx={{ fontSize: '0.85rem', color: theme.text, mb: 1.25, whiteSpace: 'pre-wrap' }}
          >
            {pendingAction.previewText}
          </Typography>

          {editing ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <TextField
                data-testid="assistant-edit-input"
                multiline
                minRows={2}
                size="small"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                fullWidth
                autoFocus
                disabled={buttonsDisabled}
              />
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  onClick={() => setEditing(false)}
                  sx={{ color: theme.gray?.[600], textTransform: 'none' }}
                >
                  Discard
                </Button>
                <Button
                  data-testid="assistant-edit-save"
                  size="small"
                  variant="contained"
                  onClick={submitEdit}
                  disabled={buttonsDisabled || !draft.trim()}
                  sx={{
                    backgroundColor: theme.primary,
                    textTransform: 'none',
                    '&:hover': { backgroundColor: theme.primary },
                  }}
                >
                  Save changes
                </Button>
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              <Button
                data-testid="assistant-action-confirm"
                size="small"
                variant="contained"
                startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon width={16} />}
                onClick={onConfirm}
                disabled={buttonsDisabled}
                sx={{
                  backgroundColor: theme.success,
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&:hover': { backgroundColor: theme.successHover || theme.success },
                }}
              >
                Confirm
              </Button>
              {pendingAction.editHref ? (
                /* Document-upload proposals: editing 8 parsed fields in chat is
                   clumsy — open the manual form prefilled instead. */
                <Button
                  data-testid="assistant-action-edit-form"
                  size="small"
                  variant="outlined"
                  startIcon={<PencilSquareIcon width={16} />}
                  href={pendingAction.editHref}
                  disabled={buttonsDisabled}
                  sx={{
                    color: theme.text,
                    borderColor: border,
                    textTransform: 'none',
                    '&:hover': { borderColor: theme.primary },
                  }}
                >
                  Edit in form
                </Button>
              ) : (
                <Button
                  data-testid="assistant-action-edit"
                  size="small"
                  variant="outlined"
                  startIcon={<PencilSquareIcon width={16} />}
                  onClick={() => setEditing(true)}
                  disabled={buttonsDisabled}
                  sx={{
                    color: theme.text,
                    borderColor: border,
                    textTransform: 'none',
                    '&:hover': { borderColor: theme.primary },
                  }}
                >
                  Edit
                </Button>
              )}
              <Button
                data-testid="assistant-action-cancel"
                size="small"
                variant="text"
                startIcon={<XCircleIcon width={16} />}
                onClick={onCancel}
                disabled={buttonsDisabled}
                sx={{ color: theme.danger, textTransform: 'none' }}
              >
                Cancel
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default AssistantMessageBubble;
